import { Injectable } from '@angular/core';
import type { SavedFilterFindInterface } from '@zaoblako/shared/modules/personalization/dtos/saved-filter/saved-filter-find.dto';
import { ReportTypeEnum } from '@zaoblako/shared/modules/personalization/enums/report.type.enum';
import type { RequiredAnalyticalFeaturesInterface } from '@zaoblako/shared/modules/catalog/dtos/required-analytical-features.dto';
import type { TenantFindInterface } from '@zaoblako/shared/modules/saas/dtos/tenant-find.dto';
import type { CommonSettingsInterface } from '@zaoblako/shared/modules/saas/dtos/common-settings.dto';
import { SessionModeEnum } from '@zaoblako/shared/modules/security/enums/session-mode.enum';
import { GlobalInfoInterface } from '@zaoblako/web/core/models/global.info.interface';
import { BasisSystemAppMetadataService } from '@zaoblako/web/core/services/basis/basis-system-app-metadata.service';
import { PersonalizationService } from '@zaoblako/web/core/services/basis/personalization.service';
import { PlanFactSettingsService } from '@zaoblako/web/core/services/budget/plan-fact-settings.service';
import { BasisSaasCommonSettingsService } from '@zaoblako/web/core/services/basis/basis-saas-common-settings.service';
import { CurrentTimeZoneService } from '@zaoblako/web/core/services/current-time-zone.service';
import { AuthService } from '@zaoblako/web/core/services/security/auth.service';
import { TenantService } from '@zaoblako/web/core/services/security/tenant.service';
import { UserService } from '@zaoblako/web/core/services/security/user.service';
import { SettingsService } from '@zaoblako/web/core/services/settings.service';
import { StoreService } from '@zaoblako/web/core/services/store.service';
import { BehaviorSubject, forkJoin, Observable, of, shareReplay, Subject, tap } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class GlobalService extends StoreService<GlobalInfoInterface> {
  protected state = new BehaviorSubject<GlobalInfoInterface>({
    isLoaded: false,
    requiredAnalyticalFeatures: null,
    tenantInfo: null,
    accessControlRules: [],
    savedFilters: [],
    config: null,
    commonSettings: null,
    planFactSettings: null
  });

  public showWelcome$: Subject<boolean> = new Subject<boolean>();

  public get globalInfo(): GlobalInfoInterface {
    return this.state.value;
  }

  public constructor(
    private readonly authService: AuthService,
    private readonly tenantService: TenantService,
    private readonly settingsService: SettingsService,
    private readonly planFactSettingsService: PlanFactSettingsService,
    private readonly userService: UserService,
    private readonly personalizationService: PersonalizationService,
    private readonly currentTimeZoneService: CurrentTimeZoneService,
    private readonly basisSystemAppMetadataService: BasisSystemAppMetadataService,
    private readonly basisSaasCommonSettingsService: BasisSaasCommonSettingsService
  ) {
    super();
  }

  public loadData(): Observable<any> {
    if (this.authService.currentProfile?.sessionMode === SessionModeEnum.TENANT) {
      return forkJoin([
        this.tenantService.getCurrent(),
        this.settingsService.getRequiredAnalyticalFeatures(),
        this.userService.getAccessControlRules(),
        this.personalizationService.getSavedFilter(),
        this.basisSystemAppMetadataService.getConfig(),
        this.basisSaasCommonSettingsService.getCommonSettings()
      ]).pipe(
        tap({
          next: ([tenantInfo, settings, accessControlRules, savedFilters, config, commonSettings]) => {
            this.updateState({
              isLoaded: true,
              requiredAnalyticalFeatures: settings,
              tenantInfo,
              accessControlRules,
              savedFilters,
              config,
              commonSettings
            });
            this.currentTimeZoneService.setTimeZone(tenantInfo.timezone);
            if (this.isEnableModule('FINANCE') && this.isAccessRule('FINANCE:PLAN_FACT:SETTINGS')) {
              this.planFactSettingsService
                .getSettings()
                .subscribe((planFactSettings) => this.updateState({ planFactSettings }));
            }
          }
        }),
        shareReplay(1)
      );
    } else {
      return of(null).pipe(
        tap({
          next: () => {
            this.updateState({
              isLoaded: true
            });
          }
        })
      );
    }
  }

  public updateRequiredAnalyticalFeatures(requiredAnalyticalFeatures: RequiredAnalyticalFeaturesInterface) {
    this.updateState({
      requiredAnalyticalFeatures: { ...requiredAnalyticalFeatures }
    });
  }

  public updateCommonSettings(commonSettings: CommonSettingsInterface): void {
    this.updateState({
      commonSettings: { ...commonSettings }
    });
  }

  public updateTenant(tenantInfo: TenantFindInterface) {
    this.updateState({
      tenantInfo: { ...tenantInfo }
    });
  }

  public reloadAccessControlRules() {
    this.userService.getAccessControlRules().subscribe((accessControlRules) => {
      this.updateState({
        accessControlRules: [...accessControlRules]
      });
    });
  }

  public isAccessRule(sysName: string): boolean {
    const access = this.globalInfo.accessControlRules?.find((acc) => acc.systemName === sysName);
    if (!access) {
      return false;
    }

    return access.isPermission ? access.isAllowed : access.level > 0;
  }

  public getFiltersByType(type: ReportTypeEnum): SavedFilterFindInterface[] {
    return this.globalInfo.savedFilters?.filter((filter) => filter.report === type);
  }

  public isEnableModule(moduleName: string): boolean {
    return (this.globalInfo.tenantInfo?.ratePlan?.availableModules || []).includes(moduleName);
  }

  public updateFilter(savedFilter: SavedFilterFindInterface) {
    const savedFilters = this.globalInfo.savedFilters?.filter((filter) => filter.id !== savedFilter.id);
    savedFilters.push(savedFilter);
    this.updateState({
      savedFilters: [...savedFilters]
    });
  }

  public addFilter(savedFilter: SavedFilterFindInterface) {
    const savedFilters = this.globalInfo.savedFilters;
    savedFilters.push(savedFilter);
    this.updateState({
      savedFilters: [...savedFilters]
    });
  }

  public deleteFilter(savedFilter: SavedFilterFindInterface) {
    const savedFilters = this.globalInfo.savedFilters?.filter((filter) => filter.id !== savedFilter.id);
    this.updateState({
      savedFilters: [...savedFilters]
    });
  }

  public setTimerShowWelcomeSystem(time: number) {
    setTimeout(() => {
      const tenantInfo = this.globalInfo.tenantInfo;
      tenantInfo.showWelcome = true;
      this.updateState({
        tenantInfo: { ...tenantInfo }
      });
      this.showWelcome$.next(this.globalInfo.tenantInfo.showWelcome);
    }, time);
  }
}
